import { getAppEnv, LogLevel, LogLevels } from '~portal/env';
import { onDatadogLoaded } from './datadog-sdk';

/**
 * Logger interface matching Datadog browser logger API
 */
export type Logger = {
  debug: (message: string, context?: object) => void;
  info: (message: string, context?: object) => void;
  warn: (message: string, context?: object) => void;
  error: (message: string, context?: object) => void;
};

type Level = keyof Logger;

/**
 * @desc How many Datadog-bound entries the logger holds while the SDK has not loaded (no analytics
 * consent yet, or the chunk still in flight). The bundled SDK buffered pre-`init` calls the same
 * way; bounded so a visitor who never consents costs a fixed amount of memory.
 */
export const PENDING_LOG_LIMIT = 100;

/**
 * Datadog logger wrapper that:
 * - Filters logs based on configured log level
 * - Also logs to console in development mode
 * - Holds Datadog-bound entries until the lazily loaded SDK arrives (`datadog-sdk.ts`), then
 *   replays them in order
 */
export class ClientLogger implements Logger {
  private ddLogger: Logger | null = null;
  private readonly pending: Array<[Level, string, object | undefined]> = [];
  private readonly allowedLevels: Set<LogLevel>;

  constructor(
    private readonly cfg: { loggingEnabled: boolean; logLevel: LogLevel },
    private readonly isDev: boolean,
  ) {
    // The configured level and everything more severe: `info` sends info, warn and error. (It used
    // to keep the levels BELOW the threshold, so the default `info` dropped every warn and error.)
    this.allowedLevels = new Set(LogLevels.slice(LogLevels.indexOf(cfg.logLevel)));
    if (cfg.loggingEnabled) {
      onDatadogLoaded(({ datadogLogs }) => {
        const ddLogger = datadogLogs.logger;
        this.ddLogger = ddLogger;
        for (const [level, message, context] of this.pending.splice(0)) {
          ddLogger[level](message, context);
        }
      });
    }
  }

  private send(level: Level, message: string, context?: object) {
    if (!this.cfg.loggingEnabled || !this.allowedLevels.has(level)) return;
    if (this.ddLogger) this.ddLogger[level](message, context);
    else if (this.pending.length < PENDING_LOG_LIMIT) this.pending.push([level, message, context]);
  }

  get shouldConsole() {
    return this.isDev || !this.cfg.loggingEnabled;
  }

  debug(message: string, context?: object) {
    if (this.shouldConsole) console.debug(message, context);
    this.send('debug', message, context);
  }

  info(message: string, context?: object) {
    if (this.shouldConsole) console.info(message, context);
    this.send('info', message, context);
  }

  warn(message: string, context?: object) {
    if (this.shouldConsole) console.warn(message, context);
    this.send('warn', message, context);
  }

  error(message: string, context?: object) {
    if (this.shouldConsole) console.error(message, context);
    this.send('error', message, context);
  }
}

/**
 * Global logger instance
 * Initialized during DatadogRUM component mount
 */
let loggerInstance: Logger;

/**
 * Get the logger instance
 * Falls back to console logging if Datadog is not initialised
 */
function getLogger(): Logger {
  if (!loggerInstance) {
    // Fallback logger before initialization
    const appEnv = getAppEnv();
    loggerInstance = new ClientLogger(appEnv.datadog, import.meta.env.DEV);
  }
  return loggerInstance;
}

/**
 * Application logger for client-side logging
 *
 * Automatically uses Datadog when enabled, falls back to console otherwise.
 * In development mode, also outputs to console for visibility.
 *
 * Usage:
 * ```ts
 * import { logger } from '~portal/lib/DatadogRUM';
 *
 * logger.debug('Debug message', { context: 'value' });
 * logger.info('Info message', { userId: 123 });
 * logger.warn('Warning message', { reason: 'something' });
 * logger.error('Error occurred', { error: err.message });
 * ```
 *
 * @see https://docs.datadoghq.com/logs/log_collection/javascript/#send-a-custom-log-entry
 */
export const logger = getLogger();
